import { createAsyncThunk } from "@reduxjs/toolkit";
import { useSelector } from "react-redux";
import {
  salesVoucherApi,
  getSalesReturnVoucherApi,
  salesRecordWithDetailsApi,
  getSalesReturnedItemsApi,
  getAvailableSerialsApi,
  getsalesReturnRecordApi,

} from "../../apiservice/sales";
import { getAccountBySeachApi, getAccountByTypeApi } from "../../apiservice/setting";
import { postData } from "../../apiservice/apis";
import { formatToCompanyTimezone } from "../../lib/functions";

export const fetchCustomersList = createAsyncThunk("salesReturn/fetchCustomersList", async (search, { getState }) => {
  const state = getState();
  const authInfo = state.auth.auth_info;
  let data = [];
  if (authInfo.subscription === "SuperERP") {
    data = ["Customer", "Patient"];
  } else if (authInfo.branch_company_type === "Pharmacy") {
    data = ["Customer", "Patient"];
  } else {
    data = ["Customer"];
  }
  const res = await getAccountBySeachApi({ query: search, type: "debitor" });
  return res.data;
});

export const fetchSalesVouchers = createAsyncThunk("salesReturn/fetchSalesVouchers", async (customerId, { rejectWithValue }) => {
  try {
    const [vouchersRes, replaceRes, returnRes] = await Promise.all([
      salesVoucherApi({ customerId }),
      getSalesReturnedItemsApi({ customerId }),
      getSalesReturnedItemsApi({ customerId, type: "replace" }),
    ]);
    const usedSaleIds = new Set([...returnRes.data, ...replaceRes.data].map((x) => x.sale_id));
    const filtered = vouchersRes.data.filter((v) => !usedSaleIds.has(v.sale_id));
    return filtered;
  } catch (error) {
    return rejectWithValue(error.response?.data || error.message);
  }
});

export const fetchSalesReturnVoucher = createAsyncThunk("salesReturn/fetchSalesReturnVoucher", async () => {
  const res = await getSalesReturnVoucherApi();
  return res.data;
});

export const fetchSalesVoucherDetails = createAsyncThunk("salesReturn/fetchSalesVoucherDetails", async (saleId, { rejectWithValue }) => {
  try {
    const [salesDetails, returnedItems] = await Promise.all([
      salesRecordWithDetailsApi(saleId),
      getSalesReturnedItemsApi({ sale_id: saleId }),
    ]);
    const saleData = salesDetails.data?.[0] || {};

    return {
      salesDetails: salesDetails.data,
      returnedItems: returnedItems.data,
      // ✅ Add Tatmeen data
      tatmeenData: {
        is_tatmeen_product: saleData.is_tatmeen_product === 1 ||
          saleData.is_tatmeen_product === "1" ||
          saleData.is_tatmeen_product === true,
        sscc_code: saleData.sscc_code || "",
        parent_sscc_code: saleData.sscc_code || "",
      }
    };
  } catch (error) {
    return rejectWithValue(error.response?.data || error.message);
  }
});

export const createSalesReturn = createAsyncThunk("salesReturn/createSalesReturn", async (payload, { rejectWithValue }) => {
  try {
    const res = await postData("/sales/create-sales-return", payload);
    return res.data;
  } catch (error) {
    return rejectWithValue(error.response?.data || error.message);
  }
});

export const updateSalesReturn = createAsyncThunk(
  "salesReturn/updateSalesReturn",
  async (payload, { rejectWithValue }) => {
    try {
      const res = await postData("/sales/update-sales-return", payload);
      return res.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchSalesReturnAccounts = createAsyncThunk("salesReturn/fetchSalesReturnAccounts", async () => {
  const res = await getAccountBySeachApi({
    multiType: ["sale_return"],
  });
  return res.data;
});

export const fetchTaxAccounts = createAsyncThunk("salesReturn/fetchTaxAccounts", async (_, { getState }) => {
  const res = await getAccountByTypeApi({
    acc_type_id: "dutie_&_tax"
  });
  return res.data;
});

export const fetchDiscountAccounts = createAsyncThunk("salesReturn/fetchDiscountAccounts", async () => {
  const res = await getAccountByTypeApi({
    acc_type_id: "direct_expense",
    acc_name: "Discount on sale",
  });
  return res.data;
});

export const fetchTransportAccounts = createAsyncThunk("salesReturn/fetchTransportAccounts", async () => {
  const res = await getAccountBySeachApi({ type: "transport" });
  return res.data;
});

export const fetchAvailableSerials = createAsyncThunk("salesReturn/fetchAvailableSerials", async ({ saleId, itemId }) => {
  const res = await getAvailableSerialsApi({ sale_id: saleId, item_id: itemId });
  return { itemId, serials: res.data };
});

export const fetchSalesReturnWithDetails = createAsyncThunk(
  "salesReturn/fetchSalesReturnWithDetails",
  async (sale_r_id, { dispatch, rejectWithValue }) => {
    try {
      const res = await getsalesReturnRecordApi({ sale_r_id, mode: "single" });
      const data = res.data;
      const [vouchersRes, replaceRes, returnRes] = await Promise.all([
        salesVoucherApi({ customerId: data?.acc_id }),
        getSalesReturnedItemsApi({ sale_id: data?.sale_id, exclude_sale_r_id: sale_r_id }),
        getSalesReturnedItemsApi({ sale_id: data?.sale_id, type: "replace" }),
      ]);

      const usedSaleIds = new Set([
        ...returnRes.data,
        ...replaceRes.data
      ].map(x => x.sale_id));

      const vouchers = vouchersRes.data.filter(v => !usedSaleIds.has(v.sale_id));

      const matchedVoucher = vouchers.find(v => v.sale_id === data?.sale_id);

      const returnQtyMap = {};
      (returnRes.data || []).forEach(r => {
        returnQtyMap[r.item_id] = Number(r.total_return_qty || 0);
      });

      const selectedSaleInvoiceDisplayText =
        matchedVoucher?.display_text || data.sale_id;
      const header = {
        sale_r_id: data.sale_r_id,
        sale_id: data.sale_id,
        sale_r_voucher_no: data.sale_r_voucher_no,
        parent_sscc_code: data.sscc_code || "",
        is_tatmeen_product: data.is_tatmeen_product || "no",
        created_date: formatToCompanyTimezone(data.created_date),
        narration: data.narration,
        taxMode: data.is_cal_type || "on_total",
        salesReturnAcc: {
          acc_id: data.sales_return_acc_id,
          acc_name: data.sales_return_acc_name,
        },
        discountAcc: {
          acc_id: data.discount_acc_id,
          acc_name: data.discount_acc_name,
        },
        taxAcc: {
          acc_id: data.tax_acc_id,
          acc_name: data.tax_acc_name,
        },
        transportAcc: {
          acc_id: data.transport_acc_id,
          acc_name: data.transport_acc_name,
        },
        customer: {
          acc_id: data.acc_id,
          acc_name: data.acc_name,
          contact_no: data.contact_no,
          address: data.address,
          institution_name: data.institution_name,
        },


        selectedSaleInvoice: {
          sale_id: data.sale_id,
          display_text: selectedSaleInvoiceDisplayText,
        },
        tags: data.tags || [],
        footerValues: {
          sale_r_id: data.sale_r_id,
          subTotal: Number(data.sub_total || 0),
          totalAmount: Number(data.total_amount || 0),
          paidAmount: Number(data.paid_amount || 0),
          serviceCharge: Number(data.total_transport_cost || 0),
          discount: Number(data.total_discount || 0),
          vat: Number(data.total_tax || 0),
          dueAmount: Number(data.total_amount || 0) - Number(data.paid_amount || 0),
          narration: data.narration || "",
          payCart: (data.trans || []).map((t, idx) => {
            const isCheque = !!t.cheque_number;
            return {
              key: t.from_acc_id || t.to_acc_id || `pay_${idx}`,
              type: t.from_acc_name || t.acc_name,
              from_acc_id: t.from_acc_id,
              from_acc_name: t.from_acc_name || t.acc_name,
              acc_type_id: isCheque ? "bank" : "cash",
              tran_amount: Number(t.tran_amount || t.amount || 0),
              cheque_number: isCheque ? t.cheque_number : "",
              date: t.created_date || t.cheque_date || data.created_date
            }
          }),

        },
      };

      const items = (data.details || []).map((d, i) => {
        const sold = Number(d.sold_qty || 0);
        const alreadyReturned = Number(returnQtyMap[d.item_id] || 0);
        const currentReturn = Number(d.sale_r_qty || 0);

        const availableQty = sold - alreadyReturned;

        const rate = Number(d.item_return_unit_price || d.sale_r_rate || d.item_rate || 0);

        return {
          key: `${d.item_id}-${i}`,
          sale_d_id: d.sale_d_id || null,
          item_id: d.item_id,
          item_name: d.item_name,
          product: d.item_name,
          display_text: d.display_text || d.item_name,
          // TOTAL SOLD
          quantity: sold,
          sold_qty: sold,
          available_qty: availableQty + currentReturn,
          // Current voucher Value
          return_quantity: currentReturn,
          rate: rate,
          item_rate: Number(d.item_rate || 0),
          item_return_unit_price: rate,
          sale_r_rate: rate,
          total: currentReturn * rate,

          item_tax: Number(d.item_tax || 0),
          item_tax_per: Number(d.item_tax_per || 0),
          discount: Number(d.item_discount || 0),
          item_discount: Number(d.item_discount || 0),

          unit_symbol: d.unit_symbol || "",
          unit_name: d.unit_name || "",
          conversion: d.conversion || 1,
          per_unit_id: d.per_unit_id || d.unit_id,

          units: d.units || [
            {
              unit_id: d.per_unit_id || d.unit_id,
              unit_symbol: d.unit_symbol || "",
              conversion: d.conversion || 1
            }
          ],

          warehouse_id: d.warehouse_id || 0,
          tax_acc_id: d.tax_acc_id || 0,
          discount_acc_id: d.discount_acc_id || 0,
          purchase_average_rate: d.purchase_average_rate || 0,
          serial_mode: d.serial_mode || "unit",

          availableSerials: Array.isArray(d.availableSerials)
            ? d.availableSerials
            : [],

          selectedSerials: Array.isArray(d.selectedSerials)
            ? d.selectedSerials
            : [],
          child_sscc_code: d.child_sscc_code || "",
          selected_batch_id: d.batch_details_id || null,
          is_tatmeen_required: d.is_tatmeen_required === 1 ||
            d.is_tatmeen_required === "1" ||
            d.is_tatmeen_required === true,
        }
      });

      return { header, items };
    } catch (err) {
      return rejectWithValue(err.response?.data || "Failed to fetch details");
    }
  }
);


